import { CurrencyAmount, Token } from '@uniswap/sdk-core';
import { Pair, Route as V2Route } from '@uniswap/v2-sdk';
import { Pool, Route as V3Route } from '@uniswap/v3-sdk';
import { nativeOnChain } from '../../../../../constants/tokens';
import { InterfaceTrade } from './types';
/**
 * Transforms a Routing API quote into an array of routes that can be used to create
 * a `Trade`.
 */
export function computeRoutes(currencyIn, currencyOut, tradeType, quoteResult) {
    if (!quoteResult || !quoteResult.route || !currencyIn || !currencyOut)
        return undefined;
    if (quoteResult.route.length === 0)
        return [];
    var parsedTokenIn = parseToken(quoteResult.route[0][0].tokenIn);
    var parsedTokenOut = parseToken(quoteResult.route[0][quoteResult.route[0].length - 1].tokenOut);
    if (parsedTokenIn.address !== currencyIn.wrapped.address)
        return undefined;
    if (parsedTokenOut.address !== currencyOut.wrapped.address)
        return undefined;
    var parsedCurrencyIn = currencyIn.isNative ? nativeOnChain(currencyIn.chainId) : parsedTokenIn;
    var parsedCurrencyOut = currencyOut.isNative ? nativeOnChain(currencyOut.chainId) : parsedTokenOut;
    try {
        return quoteResult.route.map(function (route) {
            if (route.length === 0) {
                throw new Error('Expected route to have at least one pair or pool');
            }
            var rawAmountIn = route[0].amountIn;
            var rawAmountOut = route[route.length - 1].amountOut;
            if (!rawAmountIn || !rawAmountOut) {
                throw new Error('Expected both amountIn and amountOut to be present');
            }
            return {
                routev3: isV3Route(route) ? new V3Route(route.map(parsePool), parsedCurrencyIn, parsedCurrencyOut) : null,
                routev2: !isV3Route(route) ? new V2Route(route.map(parsePair), parsedCurrencyIn, parsedCurrencyOut) : null,
                inputAmount: CurrencyAmount.fromRawAmount(parsedCurrencyIn, rawAmountIn),
                outputAmount: CurrencyAmount.fromRawAmount(parsedCurrencyOut, rawAmountOut),
            };
        });
    }
    catch (e) {
        // `Route` constructor may throw if inputs/outputs are temporarily out of sync
        // (RTK-Query always returns the latest data which may not be the right inputs/outputs)
        // This is not fatal and will fix itself in future render cycles
        console.error(e);
        return undefined;
    }
}
export function transformRoutesToTrade(route, tradeType, gasUseEstimateUSD) {
    var _a, _b;
    return new InterfaceTrade({
        v2Routes: (_a = route === null || route === void 0 ? void 0 : route.filter(function (r) { return r.routev2 !== null; }).map(function (_a) {
            var routev2 = _a.routev2, inputAmount = _a.inputAmount, outputAmount = _a.outputAmount;
            return ({ routev2: routev2, inputAmount: inputAmount, outputAmount: outputAmount });
        })) !== null && _a !== void 0 ? _a : [],
        v3Routes: (_b = route === null || route === void 0 ? void 0 : route.filter(function (r) { return r.routev3 !== null; }).map(function (_a) {
            var routev3 = _a.routev3, inputAmount = _a.inputAmount, outputAmount = _a.outputAmount;
            return ({ routev3: routev3, inputAmount: inputAmount, outputAmount: outputAmount });
        })) !== null && _b !== void 0 ? _b : [],
        tradeType: tradeType,
        gasUseEstimateUSD: gasUseEstimateUSD,
    });
}
var parseToken = function (_a) {
    var address = _a.address, chainId = _a.chainId, decimals = _a.decimals, symbol = _a.symbol;
    return new Token(chainId, address, parseInt(decimals.toString()), symbol);
};
var parsePool = function (_a) {
    var fee = _a.fee, sqrtRatioX96 = _a.sqrtRatioX96, liquidity = _a.liquidity, tickCurrent = _a.tickCurrent, tokenIn = _a.tokenIn, tokenOut = _a.tokenOut;
    return new Pool(parseToken(tokenIn), parseToken(tokenOut), parseInt(fee), sqrtRatioX96, liquidity, parseInt(tickCurrent));
};
var parsePair = function (_a) {
    var reserve0 = _a.reserve0, reserve1 = _a.reserve1;
    return new Pair(CurrencyAmount.fromRawAmount(parseToken(reserve0.token), reserve0.quotient), CurrencyAmount.fromRawAmount(parseToken(reserve1.token), reserve1.quotient));
};
function isV3Route(route) {
    return route[0].type === 'v3-pool';
}
